import React, { useState, useEffect, useCallback } from 'react';
import Icon from './Icon';
import ConfirmDialog from './ui/ConfirmDialog';
import StatusBadge from './ui/StatusBadge';
import SearchableSelect from './ui/SearchableSelect';
import { useNotifications } from './NotificationProvider';
import {
  BotGroup,
  BotGroupRole,
  CreateBotGroup,
  listBotGroups,
  addBotGroup,
  updateBotGroup,
  deleteBotGroup,
} from '../services/BotGroupsService';
import '../styles/components/BotGroupsSettings.css';
import EmptyState from './EmptyState';

const ROLE_LABELS: Record<BotGroupRole, string> = {
  authority: 'Authority',
  ops: 'Ops coordination',
  family: 'Family',
  silent: 'Silent (never send)',
};

const ROLE_DESCRIPTIONS: Record<BotGroupRole, string> = {
  authority: 'Sends decision requests here for approval',
  ops: 'Receives general coordination messages',
  family: 'Passive updates only, no task messages',
  silent: 'Ano will never send to this group',
};

const EMPTY_FORM: CreateBotGroup = { jid: '', name: '', role: 'ops', enabled: true, notes: '' };

export default function BotGroupsSettings() {
  const { showError } = useNotifications();
  const [groups, setGroups] = useState<BotGroup[]>([]);
  const [loading, setLoading] = useState(true);
  // Only the load failure stays inline — it stands in for the group list
  // when there is nothing to show. Add/toggle/role/delete failures are
  // transient and go through the app-wide toast provider.
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState<CreateBotGroup>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listBotGroups();
      setGroups(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = () => setShowAddForm(true);
    window.addEventListener('bgs-open-add-form', handler);
    return () => window.removeEventListener('bgs-open-add-form', handler);
  }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.jid.trim() || !form.name.trim()) return;
    setSaving(true);
    try {
      await addBotGroup({ ...form, jid: form.jid.trim(), name: form.name.trim() });
      setForm(EMPTY_FORM);
      setShowAddForm(false);
      await load();
    } catch (err) {
      showError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (group: BotGroup) => {
    try {
      await updateBotGroup(group.id, { enabled: !group.enabled });
      await load();
    } catch (err) {
      showError((err as Error).message);
    }
  };

  const handleChangeRole = async (group: BotGroup, role: BotGroupRole) => {
    try {
      await updateBotGroup(group.id, { role });
      await load();
    } catch (err) {
      showError((err as Error).message);
    }
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    setConfirmDeleteId(null);
    setDeletingId(id);
    try {
      await deleteBotGroup(id);
      await load();
    } catch (err) {
      showError((err as Error).message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="bgs-root">
      <p className="settings-section-description bgs-description">
        Ano will only send messages to groups explicitly listed here.
        Add groups Ano is a member of and assign each a role.
      </p>

      {error && (
        <div className="bgs-error">
          {error}
          <button onClick={() => setError(null)} className="bgs-error-close">✕</button>
        </div>
      )}

      {loading ? (
        <p className="bgs-loading">Loading groups…</p>
      ) : (
        <>
          {groups.length === 0 && !showAddForm && (
            <EmptyState
              compact
              title="No approved groups yet"
              description="Add a WhatsApp group JID to let Ano contact it."
            />
          )}

          {groups.length > 0 && (
            <div className="bgs-list">
              {groups.map(group => (
                <div key={group.id} className={`bgs-group${group.enabled ? '' : ' bgs-group--disabled'}`}>
                  <div className="bgs-group-header">
                    <div className="bgs-group-title-row">
                      <span className="bgs-group-name">{group.name}</span>
                      <StatusBadge variant={group.enabled ? 'success' : 'neutral'}>
                        {group.enabled ? 'Active' : 'Disabled'}
                      </StatusBadge>
                    </div>
                    <div className="bgs-group-jid">{group.jid}</div>
                  </div>

                  <div className="bgs-group-role-row">
                    <label className="bgs-role-label">Role</label>
                    <SearchableSelect
                      className="bgs-role-select"
                      value={group.role}
                      onChange={value => handleChangeRole(group, value as BotGroupRole)}
                      options={(Object.keys(ROLE_LABELS) as BotGroupRole[]).map(role => ({ value: role, label: ROLE_LABELS[role] }))}
                    />
                    <span className="bgs-role-desc">— {ROLE_DESCRIPTIONS[group.role]}</span>
                  </div>

                  {group.notes && (
                    <div className="bgs-group-notes">{group.notes}</div>
                  )}

                  <div className="bgs-group-actions">
                    <button
                      onClick={() => handleToggleEnabled(group)}
                      className="secondary-btn bgs-toggle-btn"
                    >
                      {group.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(group.id)}
                      disabled={deletingId === group.id}
                      className="bgs-remove-btn"
                    >
                      {deletingId === group.id ? '…' : 'Remove'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showAddForm ? (
            <form className="bgs-add-form" onSubmit={handleAdd}>
              <h4 className="bgs-add-title">Add Group</h4>
              <div className="bgs-add-fields">
                <div className="form-group">
                  <label>
                    Group JID
                    <span className="bgs-field-hint"> — e.g. 18327200586-1502379070@g.us</span>
                  </label>
                  <input
                    type="text"
                    value={form.jid}
                    onChange={e => setForm(f => ({ ...f, jid: e.target.value }))}
                    placeholder="18327200586-1502379070@g.us"
                    required
                    className="bgs-input-mono"
                  />
                  <span className="bgs-field-hint bgs-field-hint-block">
                    Find the JID in Ano-Bot dashboard or ask Ano to report the group JID.
                  </span>
                </div>
                <div className="form-group">
                  <label>Display Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Ekonce Planning Group"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <SearchableSelect
                    value={form.role}
                    onChange={value => setForm(f => ({ ...f, role: value as BotGroupRole }))}
                    options={(Object.entries(ROLE_LABELS) as [BotGroupRole, string][]).map(([role, label]) => ({ value: role, label: `${label} — ${ROLE_DESCRIPTIONS[role]}` }))}
                  />
                </div>
                <div className="form-group">
                  <label>Notes <span className="bgs-field-hint">(optional)</span></label>
                  <input
                    type="text"
                    value={form.notes || ''}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="e.g. Main planning group — Oce, Nefy, Ibrahim"
                  />
                </div>
              </div>
              <div className="bgs-form-footer">
                <button type="submit" disabled={saving} className="primary-btn">
                  {saving ? 'Adding…' : 'Add Group'}
                </button>
                <button type="button" onClick={() => { setShowAddForm(false); setForm(EMPTY_FORM); }} className="secondary-btn">
                  Cancel
                </button>
              </div>
            </form>
          ) : null}
        </>
      )}

      <div className="bgs-jid-hint">
        <strong>How to find a group JID:</strong> The JID is a unique identifier for a WhatsApp group.
        It looks like <code>18327200586-1502379070@g.us</code>.
      </div>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={() => confirmDeleteId && handleDelete(confirmDeleteId)}
        title="Remove group?"
        description="Remove this group from the approved list?"
        confirmLabel="Remove"
      />
    </div>
  );
}
